import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { getPaymentConfig, startCheckout } from "@/lib/api";
import { useSpark } from "@/lib/store";
import { BrandHeader } from "@/components/BrandHeader";
import { PublicFooter } from "@/components/PublicFooter";

const INCLUDED = [
  "Conversation 1: Why am I here? — your Starting Point, in your own words",
  "Strengths, Energy and Values maps built from your real evidence",
  "Your Gold Thread — the pattern that runs through all of it",
  "Your Career Compass and Part Two: Make My Move",
  "Full access, at your own pace — no subscription",
];

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e?.msg).filter(Boolean).join(" ") : "Something went wrong. Please try again.");

export default function Start() {
  const nav = useNavigate();
  const { isPilot } = useSpark();
  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const cancelled = new URLSearchParams(window.location.search).get("cancelled") === "1";

  useEffect(() => { document.title = "Find my next step — Spark Coaching"; getPaymentConfig().then(setCfg).catch(() => setCfg({ amount: 99, currency: "AUD" })); }, []);
  useEffect(() => { if (isPilot) nav("/dashboard", { replace: true }); }, [isPilot, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending"); setError("");
    try {
      const { checkout_url } = await startCheckout(email.trim());
      window.location.href = checkout_url;
    } catch (err) {
      setError(errText(err?.response?.data?.detail)); setState("idle");
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader links={[{ label: "How it works", href: "/#how" }, { label: "Privacy", to: "/welcome-note?review=1" }]} />
      <main className="flex-1" style={{ background: "hsl(var(--spark-cool))" }}>
        <div className="spark-container py-16 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16 items-start">
            <div className="lg:col-span-6">
              <p className="spark-eyebrow-copper">Spark Intro · full access</p>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">Find my next step.</h1>
              <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                Guided, self-paced career coaching for people who feel stuck, are questioning their direction, or are ready for a change. One payment, everything included.
              </p>
              <ul className="mt-8 space-y-3">
                {INCLUDED.map(i => (
                  <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-[#17171C]">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-black text-white"><Check size={12} strokeWidth={2.5} /></span>
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-6">
              <div data-testid="start-card" className="spark-card px-7 py-9 sm:px-10 sm:py-11">
                <div className="flex items-baseline justify-between gap-4">
                  <p className="spark-eyebrow">Spark Intro</p>
                  <p data-testid="start-price" className="spark-display text-4xl sm:text-5xl font-bold tracking-tight">
                    {cfg ? `A$${Math.round(cfg.amount)}` : "A$99"}{" "}
                    <span className="ml-2 text-sm font-medium tracking-normal" style={{ color: "hsl(var(--spark-ink-muted))" }}>{cfg?.currency || "AUD"} · one-time</span>
                  </p>
                </div>
                {cancelled && <p data-testid="start-cancelled" className="mt-5 text-sm" style={{ color: "hsl(var(--spark-ink-soft))" }}>No charge was made. Whenever you're ready, you can pick up right here.</p>}
                <form onSubmit={submit} className="mt-7">
                  <label className="spark-eyebrow block mb-2" htmlFor="start-email">Your email</label>
                  <input id="start-email" data-testid="start-email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
                         className="w-full rounded-xl border bg-white px-4 py-3.5 text-base focus:outline-none focus:ring-2 transition-shadow"
                         style={{ borderColor: "hsl(var(--input))", "--tw-ring-color": "rgba(44,80,160,0.35)" }} />
                  <button data-testid="start-checkout-btn" disabled={state === "sending"} className="spark-btn-primary mt-5 w-full disabled:opacity-60">
                    {state === "sending" ? "Opening secure checkout…" : "Continue to secure payment"}
                  </button>
                  {error && <p data-testid="start-error" className="mt-3 text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
                </form>
                <p className="mt-5 text-xs leading-relaxed" style={{ color: "hsl(var(--spark-ink-muted))" }}>
                  Payment is handled securely by Stripe. Apple Pay and Google Pay appear automatically on supported devices. After payment you'll set a password and begin straight away.
                </p>
                <p className="mt-6 pt-5 text-sm spark-rule" style={{ color: "hsl(var(--spark-ink-soft))" }}>
                  Already have an account? <Link to="/signin" data-testid="start-signin-link" className="font-medium underline underline-offset-2 text-[#2C50A0]">Sign in</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
